import {
  apiService,
  type GroupNodeDTO,
  type GroupsResponse,
  type LuckPermsGroupDTO,
  type ProjectGroupDTO,
  type ProjectGroupsEntryDTO,
  type RankSuccessResponse,
} from '@/services/api.service';
import { memberGroups, normalizeGameGroupKey, type GameGroupLabel } from '@/utils/gameGroups';

// ============================================================
// Grades LuckPerms d'un groupe de jeu IMPOSÉ (NationsGlory : Nova, Iris)
// ============================================================
// Chaque groupe a son LuckPerms, derrière son satellite. Deux écrans visent un groupe précis
// plutôt que le groupe actif :
//   - Supervision > Grades : un cadre par groupe, chaque requête du cadre vise SON groupe, et
//     « Créer aussi sur Iris » à la création d'un grade (une requête par groupe) ;
//   - Paramètres > Grades protégés : l'union des grades de tous les groupes.
// La requête porte `X-Game-Group` = le groupe visé (option `gameGroup` du service API), la
// passerelle reste juge (400 si le groupe n'existe plus, 503 si sa cible est inutilisable, 403 hors
// de l'affectation du membre du staff). Même transport que le reste du panel (session, refus de
// groupe signalé au layout). Pour un jeu sans groupes (Paladium), ces appels ne sont jamais faits,
// et l'option y serait ignorée.
// ============================================================

type Res<T> = { data?: T; error?: string; status?: number; code?: string };

/** Grades LuckPerms du groupe `group`, quel que soit le groupe actif. */
export function listGradesOn(group: string): Promise<Res<GroupsResponse>> {
  return apiService.getAllGroups({ gameGroup: group });
}

/** Crée le grade `name` dans le LuckPerms du groupe `group`. */
export function createGradeOn(name: string, group: string): Promise<Res<RankSuccessResponse>> {
  return apiService.createGroup(name, { gameGroup: group });
}

// ---- Fonctions pures ----

/** Un grade de l'union, avec les groupes (clés, ordre des groupes) où il existe. */
export interface GradeAcrossGroups {
  name: string;
  groups: string[];
}

/**
 * Union des grades de plusieurs groupes, triée par nom. Deux grades de même nom à la casse près
 * n'en font qu'un (LuckPerms compare sans casse) ; le premier groupe donne la graphie. Un groupe
 * illisible (grades null) n'apporte rien.
 */
export function unionGrades(perGroup: { group: string; grades: LuckPermsGroupDTO[] | null | undefined }[]): GradeAcrossGroups[] {
  const byKey = new Map<string, GradeAcrossGroups>();
  for (const { group, grades } of perGroup) {
    for (const g of grades ?? []) {
      const key = g.name.toLowerCase();
      const cur = byKey.get(key);
      if (!cur) byKey.set(key, { name: g.name, groups: [group] });
      else if (!cur.groups.includes(group)) cur.groups.push(group);
    }
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Poids d'un grade d'après ses nodes : le `weight.N` actif, le plus grand s'il y en a plusieurs
 * (comme LuckPerms), sinon null. Même règle que le satellite (LuckPermsService.weightOf).
 */
export function weightFromNodes(nodes: GroupNodeDTO[]): number | null {
  let weight: number | null = null;
  for (const n of nodes) {
    if (n.value === false) continue;
    const m = /^weight\.(-?\d+)$/i.exec(n.key.trim());
    if (!m) continue;
    const w = Number.parseInt(m[1]!, 10);
    if (weight === null || w > weight) weight = w;
  }
  return weight;
}

/**
 * Grades du plus lourd au plus léger, comme LuckPerms les classe (admin en haut, default en bas).
 * Un grade sans poids compte pour 0, comme dans LuckPerms ; à poids égal, ordre du nom.
 */
export function sortGradesByWeight<T extends { name: string; weight?: number | null }>(grades: T[]): T[] {
  return [...grades].sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0) || a.name.localeCompare(b.name));
}

/** Grade in-game qu'un non-admin global ne peut ni éditer ni faire hériter : `admin`, ou protégé. */
export function isProtectedGrade(name: string, protectedGrades: string[]): boolean {
  const g = name.trim().toLowerCase();
  return g === 'admin' || protectedGrades.includes(g);
}

/**
 * Motif du refus d'un node qu'un non-admin global ajoute à un grade, ou null s'il est permis.
 * Miroir de RankNodeGuard (passerelle) : héritage ou droit d'attribuer un grade protégé, joker total,
 * nodes regex et administration de LuckPerms. Le serveur reste juge ; ce test donne seulement le
 * motif en clair au lieu d'un refus nu.
 */
export function adminOnlyNodeReason(raw: string, protectedGrades: string[]): string | null {
  const node = raw.trim().toLowerCase();
  if (!node) return null;
  if (node === '*' || node.startsWith('r=') || node === 'luckperms' || node.startsWith('luckperms.')) {
    return `Le node « ${raw.trim()} » donne la main sur LuckPerms : réservé aux admins du panel.`;
  }
  const after = (prefix: string) => (node.startsWith(prefix) ? node.slice(prefix.length).replace(/\s+/g, '') : null);
  const guarded = (g: string) => g !== '' && (g.includes('*') || isProtectedGrade(g, protectedGrades));
  const inherited = after('group.');
  if (inherited !== null && guarded(inherited)) {
    return `Hériter du grade protégé « ${inherited} » est réservé aux admins du panel.`;
  }
  const grantable = after('recube.allow-rank.');
  if (grantable !== null && guarded(grantable)) {
    return `Le droit d'attribuer le grade protégé « ${grantable} » est réservé aux admins du panel.`;
  }
  return null;
}

/** Issue de la création d'un grade sur un groupe. */
export interface CreateOutcome {
  label: string;
  ok: boolean;
  error?: string;
}

/**
 * Résumé d'une création multi-groupes : « Grade « modo » créé sur Nova et Iris. », ou, avec des
 * échecs, « Grade « modo » créé sur Nova ; échec sur Iris (motif). ».
 */
export function createSummary(name: string, outcomes: CreateOutcome[]): { ok: boolean; message: string } {
  const done = outcomes.filter((o) => o.ok).map((o) => o.label);
  const failed = outcomes.filter((o) => !o.ok);
  const list = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`);
  const failText = failed.map((o) => `${o.label}${o.error ? ` (${o.error.replace(/[.\s]+$/, '')})` : ''}`).join(', ');
  if (failed.length === 0) return { ok: true, message: `Grade « ${name} » créé sur ${list(done)}.` };
  if (done.length === 0) return { ok: false, message: `Grade « ${name} » : échec sur ${failText}.` };
  return { ok: false, message: `Grade « ${name} » créé sur ${list(done)} ; échec sur ${failText}.` };
}

// ---- Cadres de la page Grades (Supervision) ----

/** Un cadre de la page Grades : le LuckPerms d'un groupe de jeu, ou celui d'un jeu sans groupes. */
export interface GradeFrame {
  /** Groupe visé par toutes les requêtes du cadre ; null = jeu sans groupes (requêtes d'avant). */
  group: GameGroupLabel | null;
  /** Motif quand la passerelle refuse ce groupe : le cadre le dit, sans aucune requête. */
  unavailable?: string;
  /** Satellite injoignable à la dernière sonde (affichage seulement, le cadre reste utilisable). */
  unhealthy?: string;
}

export type GradeFramesPlan =
  | { kind: 'loading' }
  | { kind: 'empty'; message: string }
  | { kind: 'frames'; frames: GradeFrame[]; notice?: string };

export interface GradeFramesInput {
  /** Le jeu peut avoir des groupes (supportsGameGroups). */
  supportsGroups: boolean;
  entry: ProjectGroupsEntryDTO | undefined;
  status: 'idle' | 'loading' | 'loaded' | 'error';
  /** Le jeu avait des groupes au dernier chargement réussi. */
  known: boolean;
  /** Groupe choisi la dernière fois : seul cadre quand les groupes sont illisibles. */
  persisted: string | null;
  /** Affectation du membre du staff (session), null = tous les groupes. */
  allowed: string[] | null;
  /** Groupe choisi dans le sélecteur du bandeau : seul cadre affiché. */
  active: string | null;
  projectName: string;
}

function frameOf(g: ProjectGroupDTO): GradeFrame {
  return {
    group: { key: g.key, label: g.label, color: g.color },
    unavailable: g.available ? undefined : (g.reason ?? `${g.label} indisponible.`),
    unhealthy: g.available && !g.healthy
      ? `${g.label} : satellite injoignable à la dernière sonde${g.healthError ? ` (${g.healthError})` : ''}.`
      : undefined,
  };
}

/**
 * Cadres de la page Grades, sans I/O :
 *   - jeu sans groupes (Paladium, Recube) : un seul cadre, sans en-tête de groupe ;
 *   - jeu à groupes : le cadre du groupe choisi dans le sélecteur (sinon le premier groupe
 *     utilisable de l'affectation) ; un groupe refusé par la passerelle garde son motif ; aucun
 *     groupe dans l'affectation : message ;
 *   - groupes pas encore lus : attente ; illisibles : le groupe choisi la dernière fois, seul (la
 *     passerelle reste juge) ;
 *   - jeu à groupes sans groupes déclarés : un seul cadre, comme un jeu sans groupes.
 */
export function planGradeFrames(i: GradeFramesInput): GradeFramesPlan {
  if (!i.supportsGroups) return { kind: 'frames', frames: [{ group: null }] };
  if (i.entry) {
    const declared = i.entry.groups.filter((g) => !g.retired);
    const mine = memberGroups(i.entry, i.allowed).filter((g) => !g.retired);
    if (mine.length > 0) {
      const active = normalizeGameGroupKey(i.active);
      const chosen = mine.find((g) => g.key === active) ?? mine.find((g) => g.available) ?? mine[0]!;
      return { kind: 'frames', frames: [frameOf(chosen)] };
    }
    if (declared.length > 0) {
      return { kind: 'empty', message: `Tu n'es affecté à aucun groupe de ${i.projectName} : demande l'accès à un responsable du panel.` };
    }
    return { kind: 'empty', message: `Aucun groupe disponible pour ${i.projectName} : déclare le satellite d'un groupe dans Paramètres > Projets.` };
  }
  if (i.status === 'idle' || i.status === 'loading') return { kind: 'loading' };
  if (i.status === 'error' && i.known) {
    const key = normalizeGameGroupKey(i.persisted);
    return key
      ? {
        kind: 'frames',
        frames: [{ group: { key, label: key } }],
        notice: `Groupes de jeu indisponibles : seul ${key}, le groupe choisi la dernière fois, est affiché.`,
      }
      : { kind: 'frames', frames: [{ group: null }], notice: 'Groupes de jeu indisponibles : le groupe racine est affiché.' };
  }
  return { kind: 'frames', frames: [{ group: null }] };
}

// ---- Union des grades de plusieurs groupes (Grades protégés) ----

/** Grades lus pour un écran commun à tous les groupes. */
export interface GradesAcrossGroups {
  grades: GradeAcrossGroups[];
  /** Grades protégés (minuscules), union des réponses. */
  protectedGrades: string[];
  /** Groupes lus quand il y en a plusieurs (union) ; vide = une lecture, sur le groupe actif. */
  unionOf: GameGroupLabel[];
  /** Groupes illisibles, « Iris (motif) » : leurs grades propres n'apparaissent pas. */
  unreadable: string[];
  /** Lecture unique (groupe actif) en échec : son motif. */
  error?: string;
}

/**
 * Lit les grades de chaque groupe de `targets` (requêtes à groupe imposé : l'union ne dépend pas du
 * groupe actif) quand il y en a plusieurs. Un seul groupe utilisable : une lecture imposée sur lui,
 * car le groupe actif peut encore être hors de l'affectation. Aucun (Paladium, jeu sans groupes) :
 * une lecture sans groupe, comme avant. Un groupe illisible n'empêche pas les autres.
 */
export async function loadGradesAcrossGroups(targets: ProjectGroupDTO[]): Promise<GradesAcrossGroups> {
  if (targets.length > 1) {
    const results = await Promise.all(targets.map((g) => listGradesOn(g.key)));
    return {
      grades: unionGrades(results.map((r, i) => ({ group: targets[i]!.key, grades: r.data?.groups }))),
      protectedGrades: [...new Set(results.flatMap((r) => r.data?.protectedGrades ?? []).map((n) => n.toLowerCase()))],
      unionOf: targets.map((g) => ({ key: g.key, label: g.label, color: g.color })),
      unreadable: results.flatMap((r, i) => (r.error ? [`${targets[i]!.label} (${r.error})`] : [])),
    };
  }
  const res = targets.length === 1 ? await listGradesOn(targets[0]!.key) : await apiService.getAllGroups();
  return {
    grades: unionGrades([{ group: '', grades: res.data?.groups }]).map((g) => ({ ...g, groups: [] })),
    protectedGrades: (res.data?.protectedGrades ?? []).map((n) => n.toLowerCase()),
    unionOf: [],
    unreadable: [],
    ...(res.error ? { error: res.error } : {}),
  };
}
